import { config } from '@cfg';
import { UserFavorite } from '@engelvoelkersamericas/api';
import isObjectEmpty from '@util/isObjectEmpty';
import {
  deleteAllSavedPropertiesIdFromLS,
  getSavePropertiesIdsByUserIdFromLS,
  getUserSavePropertyDetailsByListingIdFromLS,
  removePropertyFromLS,
  savePropertiesIdsByUserId,
} from '@util/toSaveProperties';
import { apiClient } from 'api-client';
import i18nConfig from 'i18n.config';
import { client } from '../../data/contentfulClient';
import auth from './auth';

export type BioDetails = {
  shopIds?: string[];
  teamPicture?: string;
  mlsIds?: any;
  associatedAdvisors: {
    key?: string;
    img: string;
    name: string;
    description: string;
    ctaUrl: string;
    ctaText: string;
  }[];
  id?: string;
  chimeInfoUserId?: string;
  chimeInfoTeamMemberId?: string;
  pronouns?: string;
  profilePicture?: string;
  name?: string;
  userId?: string;
  specializations?: string[];
  designations?: string[];
  languages?: string[];
  contact?: {
    email?: string;
    alternativeEmail?: string;
    phone?: string;
    website?: string;
  };
  associatedShops?: {
    address?: string;
    name?: string;
    locationName?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    url: string;
  };
  socialProfiles?: [];
  advisorBio?: {
    value?: string;
  };
  advisorRatings?: {}[];
  title: string;
  evTitle: string;

  positions?: string[];
  stateLicenses?: string[];
};

type ListingParams = {
  select?: string;
  pageSize?: number;
  pageNumber?: number;
  sortKey?: string;
  sortDir?: string;
  expand?: string;
  limit?: number;
  offset?: number;
  name?: string;
  shops?: string;
  languages?: string;
  specializations?: string;
  positions?: string;
  group?: string;
  state?: string;
  city?: string;
  teamIds?: string;
  shopIds?: string;
  agentIds?: string;
};

export type PropertyDetailParams = {
  select?: string;
  expand?: string;
  locale?: string;
  locationData?: boolean;
  IsDevelopment?: string;
};

export type SearchListingsParams = {
  place?: Record<string, any>;
  filter?: Record<string, any>;
  enhancementFilter?: Record<string, any>;
  sortKey?: string;
  sortDir?: string;
  pageNumber?: number;
  pageSize?: number;
  expand?: string;
  neighborhood?: string;
};

export type SearchListingsUrlParams = {
  expand?: string;
  select?: string;
  shopIds?: string;
  shopListingsOnly?: boolean;
};

type ConvertCurrencyParams = {
  baseCurrency: string;
  locale?: string;
  amount?: any;
};

type ConvertAreaParams = {
  area: number;
  unit: 'sq.ft' | 'm²';
  locale?: string;
  convert?: boolean;
};

type FormatNumberParams = {
  locale: string;
  type: 'currency' | 'percent' | 'decimal';
  number: any;
  currency?: string;
};

type AdvisorListingsParams = {
  status?: string;
};

type MarketInsightsUrlParams = {
  dataType?: string;
  isTimeseries?: boolean;
  regionType?: string;
};

export function uppercaseAfterDash(str) {
  if (!str.includes('-')) {
    return str;
  }

  return str
    .split('-')
    .map((word, index) => {
      return index === 0 ? word : word.toUpperCase();
    })
    .join('-');
}

let cacheMarketInsightData = {};

const tempCurrencyData = {
  ARS: 998.2854950000001,
  BSD: 1,
  BZD: 2.021355,
  CAD: 1.40149,
  CLP: 978.83377,
  COP: 4433.302665,
  CRC: 511.906965,
  EUR: 0.94503,
  KYD: 0.83333,
  MXN: 20.430725,
  PEN: 3.793485,
  UYU: 42.404915,
  USD: 1,
};

class evAPI {
  private _options: {};
  root: this;
  private _url: string;
  private _personnelServiceUrl: string;

  constructor(url = config.ev.url!, options = {}) {
    this.root = this;
    this._url = url;
    this._options = options;
    this._personnelServiceUrl = process.env.NEXT_PUBLIC_PERSONNEL_SERVICE_URL!;
  }

  async searchListings(params: SearchListingsParams = {}, urlParams: SearchListingsUrlParams = {}) {
    // Automatically filter out listings that are in development unless a specific filter is provided
    if (typeof params.enhancementFilter?.IsDevelopment === 'undefined') {
      params.enhancementFilter = {
        ...params.enhancementFilter,
        IsDevelopment: { ne: true },
      };
    }

    let res = await post(`${this._url}/listings/search?${toUrlParams(urlParams)}`, params);
    res = {
      total: res.total,
      resoProperties: res.resoProperties?.map((rs) => ({
        ...rs.listing,
        evaMetadata: rs.evaMetadata,
      })),
    };

    return addPagination(res, (setParams) => this.searchListings(setParams), params);
  }

  async listFavoriteProperties(sortDir, isCachedData = false): Promise<UserFavorite[] | null> {
    const user = await auth.getUser();
    const userId = user ? user.userId : null;

    if (!userId) {
      return null;
    }

    if (isCachedData) {
      const savePropertiesObj = getSavePropertiesIdsByUserIdFromLS(userId);
      if (!isObjectEmpty(savePropertiesObj) && savePropertiesObj?.savePropertyIds) {
        return Object.values(savePropertiesObj.savePropertyIds);
      }
    } else {
      deleteAllSavedPropertiesIdFromLS();
    }

    const { data: favorites } = await apiClient.getFavorites({
      userId,
    });
    if (favorites && favorites.length) {
      savePropertiesIdsByUserId(userId, favorites);
    }
    return favorites;
  }

  async saveListing(userId: string, listingId: string, listOfficeMlsId: string) {
    const { data: newFavorite } = await apiClient.saveFavorite({
      userId,
      createUserFavoriteDto: { listingId, listOfficeMlsId },
    });
    savePropertiesIdsByUserId(userId, [newFavorite]);
    return newFavorite;
  }

  async deleteSavedListing(userId: string, listingId: string, favId?: string) {
    try {
      if (!favId) {
        const propertyDetail = getUserSavePropertyDetailsByListingIdFromLS(userId, listingId);
        if (propertyDetail?.FavoriteId) {
          favId = propertyDetail?.FavoriteId;
        }
      }

      const { data: res } = await apiClient.deleteFavorite({
        userId,
        favoriteId: favId!,
      });
      removePropertyFromLS(userId, listingId);
      return res;
    } catch (err) {
      console.error({ err });
      return null;
    }
  }

  async listingByShop(shop = '', params: ListingParams = {}) {
    let res = await get(`${this._url}/listings/shop/${shop}?${toUrlParams(params)}`);
    res = {
      total: res.total,
      resoProperties: res.resoProperties.map((rs) => ({
        ...rs.listing,
        evaMetadata: rs.evaMetadata,
      })),
    };

    return addPagination(res, (setParams) => this.listingByShop(shop, setParams), params);
  }

  async listingByAgent(agentIds = '', params: ListingParams = {}) {
    let res = await get(`${this._url}/listings/agent/${agentIds}?${toUrlParams(params)}`);
    res = {
      total: res.total,
      resoProperties: res.resoProperties.map((rs) => ({
        ...rs.listing,
        evaMetadata: rs.evaMetadata,
      })),
    };
    return addPagination(res, (setParams) => this.listingByAgent(agentIds, setParams), params);
  }
  async propertyDetails(path = '', params: PropertyDetailParams = {}) {
    const mls = path.split('/')[0];
    const id = path.split('/')[1];

    if (!mls || !id) {
      return null;
    }
    let resData;

    const res = await apiClient.getListingDetailsById({
      mls,
      id,
      ...params,
    });

    resData = {
      ...res.data,

      resoProperty: {
        ...res.data.listing,
        evaMetadata: res.data.evaMetadata,
      },
    };

    return resData;
  }

  async listAdvisors(
    params: ListingParams = {},
  ): Promise<{ records: any[]; totalRecords: number }> {
    const isHeaderIncluded = false;
    const res = await get(
      `${this._personnelServiceUrl}/advisors?${toUrlParams(params)}`,
      isHeaderIncluded,
    );
    return addOffsetPagination(res, (setParams) => this.listAdvisors(setParams), params);
  }
  async advisorDetails(id: string) {
    let res = await get(`${this._personnelServiceUrl}/advisors/${id}`);
    return res;
  }
  async advisorDetailsOSNandMLSId(ids: { osn: string; agentMlsId: string }[]) {
    const isHeaderIncluded = false;
    const idString = ids.map((id) => `${id.osn}-${id.agentMlsId}`).join(',');
    let res = await get(
      `${this._personnelServiceUrl}/advisors/?agents=${idString}`,
      isHeaderIncluded,
    );
    return res;
  }
  async advisorListings(agent: string, params: AdvisorListingsParams = {}) {
    let res = await get(
      `https://property-console-api.evipscloud.com/v1/listings/agent/${agent}?${toUrlParams(
        params,
      )}`,
    );
    return res;
  }
  async locale() {
    const locale = i18nConfig.defaultLocale;
    return uppercaseAfterDash(locale);
  }
  async convertCurrency(params: ConvertCurrencyParams) {
    let resData;
    try {
      const res = await apiClient.getCurrencyConversions({ baseCurrency: params.baseCurrency });
      resData = res.data;
    } catch (err) {
      resData = tempCurrencyData;
    }
    if (params.amount != null) {
      let keys = Object.keys(resData);
      let list = {};
      let locale = params.locale == null ? 'en-US' : params.locale;
      keys.forEach((item) => {
        let amt = parseFloat(resData[item]) * parseFloat(params.amount);
        let formatAmt = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: item,
        }).format(amt);
        list[item] = formatAmt;
      });
      return list;
    } else {
      return resData;
    }
  }

  formatNumber(params: FormatNumberParams) {
    let options = {};
    let currency = params.currency ? params.currency : 'USD';
    switch (params.type) {
      case 'currency':
        options = { style: 'currency', currency: currency };
        break;
      case 'percent':
        options = { style: 'percent' };
        break;
      case 'decimal':
        options = { style: 'decimal' };
        break;
      default:
        break;
    }

    return new Intl.NumberFormat(params.locale, options).format(parseFloat(params.number));
  }

  async advisorBio(id: string) {
    const isHeaderIncluded = false;
    let res = await get(`${this._personnelServiceUrl}/advisors/${id}`, isHeaderIncluded);
    // INFO: Log response for server side call
    console.log(JSON.stringify(res));
    return res;
  }
  async advisorTeamBio(id: string) {
    const isHeaderIncluded = false;
    let res = await get(
      `https://eva-teams-service.evipscloud.com/v1/teams/${id}/roster`,
      isHeaderIncluded,
    );
    // INFO: Log response for server side call
    console.log(JSON.stringify(res));
    return res;
  }
  async mlsCompList() {
    let res = await client().getEntries({
      content_type: 'mlsComplianceData',
      limit: 1000,
    });
    return res;
  }

  async searchShops(params: ListingParams = {}) {
    const isOffsetTypeParam = true;
    const res = await apiClient.getShopsByPlace(params);

    return addPagination(
      res.data,
      (setParams) => this.searchShops(setParams),
      params,
      isOffsetTypeParam,
    );
  }

  async marketInsightsDaysOfInventoryCount(
    zipcode: string,
    urlParams: MarketInsightsUrlParams = {},
    isCached = false,
  ) {
    const key = `${zipcode}-marketInsightsDaysOfInventoryCount`;
    if (isCached && cacheMarketInsightData[key]) {
      return cacheMarketInsightData[key];
    }
    let res = await get(
      `${config.ev.url}/market-insights/days-of-inventory-count/${zipcode}?${toUrlParams(
        urlParams,
      )}`,
    );
    cacheMarketInsightData = { ...cacheMarketInsightData, [key]: res };
    return res;
  }
  async marketInsightsInventoryCount(
    zipcode: string,
    urlParams: MarketInsightsUrlParams = {},
    isCached = false,
  ) {
    const key = `${zipcode}-marketInsightsInventoryCount`;
    if (isCached && cacheMarketInsightData[key]) {
      return cacheMarketInsightData[key];
    }
    let res = await get(
      `${config.ev.url}/market-insights/inventory-count/${zipcode}?${toUrlParams(urlParams)}`,
    );
    cacheMarketInsightData = { ...cacheMarketInsightData, [key]: res };
    return res;
  }
  async marketInsightsMedianDaysOnMarket(
    zipcode: string,
    urlParams: MarketInsightsUrlParams = {},
    isCached = false,
  ) {
    const key = `${zipcode}-marketInsightsMedianDaysOnMarket`;
    if (isCached && cacheMarketInsightData[key]) {
      return cacheMarketInsightData[key];
    }
    let res = await get(
      `${config.ev.url}/market-insights/median-days-on-market/${zipcode}?${toUrlParams(urlParams)}`,
    );
    cacheMarketInsightData = { ...cacheMarketInsightData, [key]: res };
    return res;
  }
  async marketInsightsMedianListPriceDollars(
    zipcode: string,
    urlParams: MarketInsightsUrlParams = {},
    isCached = false,
  ) {
    const key = `${zipcode}-marketInsightsMedianListPriceDollars`;
    if (isCached && cacheMarketInsightData[key]) {
      return cacheMarketInsightData[key];
    }

    let res = await get(
      `${config.ev.url}/market-insights/median-list-price-dollars/${zipcode}?${toUrlParams(
        urlParams,
      )}`,
    );
    cacheMarketInsightData = { ...cacheMarketInsightData, [key]: res };
    return res;
  }
  async marketInsightsPricePerSqFtDollars(
    zipcode: string,
    urlParams: MarketInsightsUrlParams = {},
    isCached = false,
  ) {
    const key = `${zipcode}-marketInsightsPricePerSqFtDollars`;
    if (isCached && cacheMarketInsightData[key]) {
      return cacheMarketInsightData[key];
    }

    let res = await get(
      `${config.ev.url}/market-insights/price-per-sqft-dollars/${zipcode}?${toUrlParams(
        urlParams,
      )}`,
    );
    cacheMarketInsightData = { ...cacheMarketInsightData, [key]: res };
    return res;
  }
  async marketInsightsSaleVsListPricePercent(
    zipcode: string,
    urlParams: MarketInsightsUrlParams = {},
    isCached = false,
  ) {
    const key = `${zipcode}-marketInsightsSaleVsListPricePercent`;
    if (isCached && cacheMarketInsightData[key]) {
      return cacheMarketInsightData[key];
    }
    let res = await get(
      `${config.ev.url}/market-insights/sale-vs-list-price-percent/${zipcode}?${toUrlParams(
        urlParams,
      )}`,
    );
    cacheMarketInsightData = { ...cacheMarketInsightData, [key]: res };
    return res;
  }

  async getNeighborhoodsByShop(shopIds: string) {
    const res = await get(this._url + `/neighborhoods/shop/${shopIds}`);
    return res;
  }

  async getNeighborhoodsBySlug(slug: string, shopIds: string) {
    const res = await get(this._url + `/neighborhoods/slug/${slug}?shopIds=${shopIds}`);
    return res;
  }
}

const toUrlParams = (params = {}) => {
  let qArr: string[] = [];
  for (let key in params) {
    qArr.push(`${key}=${params[key]}`);
  }
  return qArr.join('&');
};

async function addPagination(
  res: any,
  fn: (params: ListingParams) => Promise<any>,
  params: ListingParams,
  isOffsetTypeParam = false,
): Promise<any> {
  const pageKey = isOffsetTypeParam ? 'offset' : 'pageNumber';
  const pageSizeKey = isOffsetTypeParam ? 'limit' : 'pageSize';
  const totalRecords = res?.total ? Number(res.total) : Number(res.totalRecords);
  let page = params[pageKey] ? params[pageKey] : 1,
    pageSize = params[pageSizeKey] ? params[pageSizeKey] : 10,
    lastPage = Math.ceil(totalRecords / pageSize);

  if (typeof res === 'object') {
    res.totalPages = lastPage;

    res.next =
      page + 1 > lastPage
        ? null
        : async () => {
            return await fn({
              ...params,
              [pageKey]: page + 1,
            });
          };

    res.prev =
      page > 1
        ? async () => {
            return await fn({
              ...params,
              [pageKey]: page - 1,
            });
          }
        : null;
  }
  res.totalRecords = Number(res.totalRecords);
  return res;
}

async function addOffsetPagination(
  res: any,
  fn: (params: ListingParams) => Promise<any>,
  params: ListingParams,
): Promise<any> {
  const totalRecords = res?.total ? res.total : res.totalRecords;
  let offset = params['offset'] || 0,
    limit = params['limit'] || 10,
    lastPage = Math.ceil(totalRecords / limit);

  if (typeof res === 'object') {
    res.totalPages = lastPage;

    res.page = async (page: number) => {
      if (page <= 0) page = 1;
      if (page > lastPage) page = lastPage;
      offset = (page - 1) * limit;
      return await fn({
        ...params,
        ['offset']: offset, // offset
      });
    };

    res.next =
      offset + limit > lastPage
        ? null
        : async () => {
            offset += limit;
            return await fn({
              ...params,
              ['offset']: offset, // offset
            });
          };

    res.prev =
      offset - limit >= 0
        ? async () => {
            offset += limit;
            return await fn({
              ...params,
              ['offset']: offset,
            });
          }
        : null;
  }

  return res;
}

const post = async (url, data = {}, callback = () => {}) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.ev.key!,
    },
    body: JSON.stringify(data),
  });
  return res.json();
};

const get = async (url, isHeaderIncluded = true) => {
  const headers = {
    'x-api-key': config.ev.key!,
  };
  const header = isHeaderIncluded ? { headers } : {};

  const res = await fetch(url, header);
  if (!res.ok) throw new Error(`HTTP error ${res.status}: ${res.statusText}`);
  //   const contentType = res.headers.get("Content-Type");
  // let data;
  // if (contentType && contentType.includes("application/json")) {
  //   data = await res.json();
  // } else {
  //   data = await res.text();
  // }
  return res.json();
};

export default evAPI;
